/**
 * used in the announcement bar, this component uses the .vertical-slide classes
 * to have messages slide in and out vertically
 */

import {
  LitElement,
  html,
  css,
  classMap,
  ref,
  createRef,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { prefersReducedMotion } = utils

class HHVerticalCarousel extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _queuedPlay: {
      state: false,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
      converter: {
        fromAttribute: (value) => {
          return Number(value * 1000)
        },
      },
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    }
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.reducedMotion = prefersReducedMotion();
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    if (
      this.speed &&
      !this.reducedMotion
    ) {
      this.setPlayPauseIntersectionObserver()
    } else if (this.speed && !this.reducedMotion) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  // Hide inactive slides from assistive tech and remove their
  // focusable elements from the tab order
  setSlideVisibility() {
    this.items.forEach((item, idx) => {
      const isActive = idx === this._current - 1
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true')

      if (item.hasAttribute('tabindex')) {
        item.setAttribute('tabindex', isActive ? '0' : '-1')
      }

      item.querySelectorAll('a, button').forEach((el) => {
        isActive ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')
      })
    })
  }

  // Autoplay methods
  setAutoplay() {
    if (!this.speed) return

    clearTimeout(this._queuedPlay);
    this._queuedPlay = setTimeout(() => {
      this.play();
    }, this.speed);

    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.runAnimation.bind(this), this.speed)
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  play() {
    this.$slides?.value?.setAttribute('aria-live', 'off')
    this.playing = true
    this.classList.remove("paused");
  }

  pause() {
    this.$slides?.value?.setAttribute('aria-live', 'polite')
    this.playing = false
    this.classList.add("paused")
  }

  runAnimation(forward = true) {
    if (forward) {
      this._current = this._current >= this._total ? 1 : this._current + 1
    } else {
      this._current = this._current <= 1 ? this._total : this._current - 1
    }

    this.items.forEach(el => el.classList.remove('active'))
    this.items[this._current - 1].classList.add('active')
  }

  setPlayPauseIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) =>
        entry.isIntersecting ? this.onFocusOut() : this.onFocusIn()
      )
    })
    observer.observe(this.$slides?.value)
  }

  // Move to next or previous slide
  move(e) {
    e.preventDefault()

    const forward = e.currentTarget?.name === 'next'
    this.userPause = true
    this.stopAutoplay()

    this.runAnimation(forward)
  }

  // Event handlers
  onFocusIn() {
    if (this.userPause || this.reducedMotion) return
    this.stopAutoplay()
  }

  onFocusOut() {
    if (this.userPause || this.reducedMotion) return
    this.setAutoplay()
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div
        class="slides"
        aria-live="polite"
        ${ref(this.$slides)}
        @mouseenter="${this.onFocusIn}"
        @mouseleave="${this.onFocusOut}"
        @focusin="${this.onFocusIn}"
        @focusout="${this.onFocusOut}"
      >
        <slot name="item"></slot>
      </div>
      ${/* Previous/next buttons */ ''}
      <div class="${classMap({ arrows: true, hidden: this._total <= 1 })}">
        <span class="visually-hidden">${t('accessibility.carousel_instructions')}</span>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="arrow arrow--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide')}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="arrow arrow--next"
          name="next"
          aria-label="${t('accessibility.next_slide')}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
      </div>`
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        position: relative;
      }

      .slides {
        height: inherit;
        overflow-y: hidden;
        position: relative;
        width: 100%;
        display: grid;
      }

      .arrows {
        align-items: center;
        bottom: 0;
        display: flex;
        justify-content: space-between;
        left: var(--hh-space-xs, 0.5rem);
        pointer-events: none;
        position: absolute;
        right: var(--hh-space-xs, 0.5rem);
        top: 0;
      }

      .arrow {
        background-color: var(--hh-color-background, #fff);
        border: none;
        color: var(--hh-color-ink, #0a0000);
        cursor: pointer;
        padding: 0.5rem 0;
        pointer-events: auto;
        position: absolute;
        top: 50%;
        border-radius: 10rem;
        width: 1.5rem;
        height: 1.5rem;
        padding: 5px;
        display: flex;
      }

      /* The arrows are visually hidden to fit the design, but stay in the
         tab order and the accessibility tree. Each one becomes visible
         while it is keyboard-focused (same pattern as skip links), so
         sighted keyboard users can see the control they're operating. */
      .arrow:not(:focus-visible) {
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        white-space: nowrap;
        width: 1px;
      }

      .arrow:focus-visible {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: -0.2rem;
      }

      .arrow--prev {
        left: 0;
        transform: translateY(-50%) rotate(90deg);
      }

      .arrow--next {
        right: 0;
        transform: translateY(-50%) rotate(-90deg);
      }
    `,
  ]
}

customElements.define('hh-vertical-carousel', HHVerticalCarousel)
